import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { StepsNodeView } from './steps-node-view'
import { nanoid } from 'nanoid'

export interface StepsAttributes {
  id?: string
  flowId?: string // Original flow ID for re-recording
  stepBlockId?: string // Step block ID if from replay
}


export const StepsSpec = {
  group: 'block',
  content: 'Step+',
  toDOM() { return ['div', { 'data-steps': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-steps]' }],
  attrs: { 
    id: { default: nanoid(10) },
    flowId: { default: null },
    stepBlockId: { default: null }
  },
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    steps: {
      /**
       * Insert a steps container
       */
      insertSteps: (attributes?: StepsAttributes) => ReturnType
      /**
       * Update steps attributes
       */
      updateSteps: (attributes: Partial<StepsAttributes>) => ReturnType
    }
  }
}

export const StepsExtension = Node.create({
  name: 'Steps',

  group: 'block',

  content: 'Step+',

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      flowId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-flow-id'),
        renderHTML: (attributes) => {
          if (!attributes.flowId) {
            return {}
          }
          return {
            'data-flow-id': attributes.flowId,
          }
        },
      },
      stepBlockId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-step-block-id'),
        renderHTML: (attributes) => {
          if (!attributes.stepBlockId) {
            return {}
          }
          return {
            'data-step-block-id': attributes.stepBlockId,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'section[data-steps-id]',
      },
      {
        tag: 'div[data-type="Steps"]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'section',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'Steps',
        class: 'relative my-5 p-0.5',
      }),
      0,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(StepsNodeView)
  },

  addCommands() {
    return {
      insertSteps:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
            content: [
              {
                type: 'Step',
                attrs: {
                  title: 'Step 1',
                  showTitle: true,
                  showIcon: false,
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
              {
                type: 'Step',
                attrs: {
                  title: 'Step 2',
                  showTitle: true,
                  showIcon: false,
                },
                content: [
                  {
                    type: 'paragraph',
                    content: [
                      
                    ],
                  },
                ],
              },
            ],
          })
        },
      updateSteps:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the steps container with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of a steps container
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
